import { ExclamationCircleFilled } from '@ant-design/icons'
import { ConfigProvider, DatePicker, Modal, Select, Skeleton, Tabs, Tag } from 'antd'
import frFR from "antd/locale/fr_FR";
import { useEffect, useReducer, useRef, useState } from 'react'
import { useReactToPrint } from 'react-to-print'
import { toast } from 'react-toastify'
import Footer from '../../../components/Footer'
import NavBar from '../../../components/NavBar'
import SideBar from '../../../components/SideBar'
import axiosInstance from '../../../variables/api/axiosInstance'
import { LOGGED_USER } from '../../../variables/constants'
import { frenchMonth, frenchDateOnly, formatJustificatifCheque } from '../../../variables/functions'
import { displayNumbers } from '../../../variables/numbersFunctions';
import BordereauEmissions from './bordereaux/BordereauEmissions'
import BordereauAnnulations from './bordereaux/BordereauAnnulations'
import BordereauSinistresPayes from './bordereaux/BordereauSinistresPayes'
import BordereauRecoursEncaisses from './bordereaux/BordereauRecoursEncaisses'
import CompteDeCession from './bordereaux/CompteDeCession'
import { useNavigate } from 'react-router-dom';

const { confirm } = Modal;

// Reducer pour gérer l'état complexe des validations
const validationReducer = (state, action) => {
  switch (action.type) {
    case 'VALIDATE_EMISSIONS':
      return { ...state, emissions: { validated: true, data: action.payload } };
    case 'VALIDATE_ANNULATIONS':
      return { ...state, annulations: { validated: true, data: action.payload } };
    case 'VALIDATE_SINISTRES':
      return { ...state, sinistresPayes: { validated: true, data: action.payload } };
    case 'VALIDATE_RECOURS':
      return { ...state, recoursEncaisses: { validated: true, data: action.payload } };
    case 'VALIDATE_CESSION':
      return { ...state, cessionValidated: true, cessionData: action.payload };
    case 'RESET':
      return {
        emissions: { validated: false, data: [] },
        annulations: { validated: false, data: [] },
        sinistresPayes: { validated: false, data: [] },
        recoursEncaisses: { validated: false, data: [] },
        cessionValidated: false,
        cessionData: null
      };
    default:
      return state;
  }
};

const initialValidationState = {
  emissions: { validated: false, data: [] },
  annulations: { validated: false, data: [] },
  sinistresPayes: { validated: false, data: [] },
  recoursEncaisses: { validated: false, data: [] },
  cessionValidated: false,
  cessionData: null
};

const Cession = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategorie, setSelectedCategorie] = useState("");
  const [reglementsByMonth, setReglementsByMonth] = useState([]);
  const [decaissements, setDecaissements] = useState([]);
  // Éléments des MOIS ANTÉRIEURS jamais cédés — proposés dans les mêmes tableaux mais DÉCOCHÉS par
  // défaut (jamais inclus par le fallback « tout le mois », qui n'utilise que reglementsByMonth /
  // decaissements). Ne sont cédés que s'ils sont explicitement cochés. Cf. endpoints /anterieurs.
  const [priorReglements, setPriorReglements] = useState([]);
  const [priorDecaissements, setPriorDecaissements] = useState([]);
  // Paramètre GLOBAL (Pool, HOP120) : autoriser la proposition des mois ANTÉRIEURS. DÉFAUT = false
  // → seul le mois de cession est proposé ; les antérieurs ne remontent que si activé en paramètre.
  const [cessionAnterieursAutorises, setCessionAnterieursAutorises] = useState(false);
  const [compagnies, setCompagnies] = useState([]);
  const [selectedCompagnie, setSelectedCompagnie] = useState("");
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState();
  const [cessionExistante, setCessionExistante] = useState("");
  const [addCessionLoading, setAddCessionLoading] = useState(false);
  const [recalculLoading, setRecalculLoading] = useState(false);
  const [tabId, setTabId] = useState("1");

  // Utilisation du reducer pour les validations
  const [validationState, dispatch] = useReducer(validationReducer, initialValidationState);

  // Sélections pour les bordereaux
  const [selectedEmissions, setSelectedEmissions] = useState([]);
  const [selectedAnnulations, setSelectedAnnulations] = useState([]);
  const [selectedSinistres, setSelectedSinistres] = useState([]);
  const [selectedRecours, setSelectedRecours] = useState([]);

  // Refs pour l'impression
  const printEmissionsRef = useRef();
  const printAnnulationsRef = useRef();
  const printSinistresRef = useRef();
  const printRecoursRef = useRef();
  const printCessionRef = useRef();

  const navigate = useNavigate();

  // Suffixe de nom de fichier PDF, aligné sur l'export Excel : <catégorie>_<Mois>_<Année>
  // (react-to-print utilise documentTitle comme nom du PDF ; sans lui, le PDF hérite du
  // titre de l'onglet — « PoolIns »).
  const docSuffix = `${selectedCategorie?.codeCategorie || ''}${date ? `_${frenchMonth(date.split('-')[1])}_${date.split('-')[0]}` : ''}`;

  // Handlers d'impression
  const handlePrintEmissions = useReactToPrint({
    content: () => printEmissionsRef.current,
    documentTitle: `Bordereau_des_emissions_${docSuffix}`,
  });

  const handlePrintAnnulations = useReactToPrint({
    content: () => printAnnulationsRef.current,
    documentTitle: `Bordereau_des_annulations_${docSuffix}`,
  });

  const handlePrintSinistres = useReactToPrint({
    content: () => printSinistresRef.current,
    documentTitle: `Bordereau_des_sinistres_payes_${docSuffix}`,
  });

  const handlePrintRecours = useReactToPrint({
    content: () => printRecoursRef.current,
    documentTitle: `Bordereau_des_recours_encaisses_${docSuffix}`,
  });

  const handlePrintCession = useReactToPrint({
    content: () => printCessionRef.current,
    documentTitle: `Compte_de_cession_${docSuffix}`,
    // Le document mesure déjà 210×297 mm (A4 plein) avec son propre padding interne.
    // On force donc l'impression SANS marge (@page margin:0), sinon les marges par défaut
    // du navigateur le débordent sur une 2e page / obligent à réduire l'échelle.
    pageStyle: `
      @page { size: A4 portrait; margin: 0; }
      @media print {
        html, body { width: 210mm; height: 297mm; margin: 0 !important; padding: 0 !important;
          -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      }
    `,
  });

  // Lecture du paramètre global « autoriser les mois antérieurs » (défaut false si indisponible).
  useEffect(() => {
    axiosInstance
      .get("/api/parametre/cession-anterieurs")
      .then((res) => setCessionAnterieursAutorises(res.data === true))
      .catch(() => setCessionAnterieursAutorises(false));
  }, []);

  useEffect(() => {
    if (LOGGED_USER.profil.organisme.typeOrganisme !== "POOL" && selectedCategorie && selectedCategorie.codeCategorie) {
      setSelectedCompagnie(LOGGED_USER.profil.organisme);
      setupDecaissements(LOGGED_USER.profil.organisme.code);
      setupReglementsOnMonth(
        LOGGED_USER.profil.organisme.code,
        (`${date}-01` || new Date().toISOString().split("T")[0])
      );
    }
  }, [selectedCategorie, date]);

  useEffect(() => {
    if (selectedCategorie && selectedCompagnie && date && selectedCompagnie.code && selectedCategorie.codeCategorie) {
      setupReglementsOnMonth(selectedCompagnie.code, `${date}-01`);
      setupDecaissements(selectedCompagnie.code);
      // Éléments des mois antérieurs non cédés (proposés décochés) — UNIQUEMENT si le paramètre
      // global (HOP120) l'autorise ; sinon on ne charge rien et on vide les listes → seul le mois
      // de cession est proposé.
      if (cessionAnterieursAutorises) {
        setupPriorReglements(selectedCompagnie.code);
        setupPriorDecaissements(selectedCompagnie.code);
      } else {
        setPriorReglements([]);
        setPriorDecaissements([]);
      }
      console.log("Invoqued");
      checkCession(`${date}-01`, selectedCompagnie?.code, selectedCategorie?.codeCategorie);
      console.log("After invocation");
      // Reset validations when filters change
      dispatch({ type: 'RESET' });
      setSelectedEmissions([]);
      setSelectedAnnulations([]);
      setSelectedSinistres([]);
      setSelectedRecours([]);
      setPriorReglements([]);
      setPriorDecaissements([]);
    }
  }, [selectedCompagnie, selectedCategorie, date, cessionAnterieursAutorises]);

  useEffect(() => {
    axiosInstance
      .get(`/api/categorie`)
      .then((res) => {
        const excludedCodes = ["A", "B", "T"];

        const filtered = res.data.filter(
          (c) =>
            !c.codeCategorie.startsWith("V") &&
            !excludedCodes.includes(c.codeCategorie)
        );

        const hasTPVCategories = res.data.some((c) =>
          excludedCodes.includes(c.codeCategorie)
        );

        if (hasTPVCategories) {
          filtered.push({
            codeCategorie: "TPV",
            nom: "TPV",
            synthetic: true,
          });
        }

        setCategories(filtered);
      })
      .catch((err) => {
        console.log(err);
      });

    axiosInstance
      .get(`/api/organisme/assurance`)
      .then((res) => {
        setCompagnies(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const setupDecaissements = (codeCompagnie) => {
    axiosInstance
      .get(`/api/decaissement/compagnie/${codeCompagnie}`, {
        params: {
          codeCategorie: selectedCategorie ? selectedCategorie.codeCategorie : null,
          mois: date ? `${date}-01` : null,
        },
      })
      .then((res) => {
        setDecaissements(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // Transforme la réponse plate (get_contrats_reglements_cession[/_anterieurs_non_cedes]) en lignes
  // de tableau. `anterieur=true` marque les éléments de mois PASSÉS (affichés avec un tag, décochés
  // par défaut, jamais inclus par le fallback « tout le mois »).
  const buildReglementRows = (data, anterieur = false) => {
    const coms = new Map();
    (data || []).forEach((contrat) => {
      if (!contrat.reglements?.length) return;
      contrat.reglements.forEach((reg, index) => {
        const key = `${contrat.codeId}-${reg.codeId}`;
        if (coms.has(key)) return;
        coms.set(key, {
          key: `${key}-${index}`,
          codeId: `${key}-${index}`,
          id: `${key}-${index}`,
          anterieur,
          categorie: contrat.categorieCode,
          numeroPolice: contrat.numeroPoliceP,
          effet: contrat.dateEffet,
          echeance: contrat.dateEcheance,
          primeACeder: reg.montant,
          // Décomposition du montant cédé (= prime TTC), proratisée côté serveur :
          // PN Pool + PN Compagnie + accessoires + taxes = primeACeder.
          primeNettePool: reg.primeNettePool,
          primeNetteCompagnie: reg.primeNetteCompagnie,
          accessoires: reg.accessoires,
          taxes: reg.taxes,
          primeTTC: reg.montant,
          avenant: reg.avenantCode || "N/A",
          assure: contrat.souscripteurPrenom
            ? `${contrat.souscripteurNom} ${contrat.souscripteurPrenom}`
            : `${contrat.souscripteurNom || ""}`,
          organisme: contrat.organismeRaisonSociale,
          immatriculation: contrat.immatriculation,
          agence: contrat.agence,
          dateEmission: contrat.dateEmission,
          intermediaire: contrat.intermediaire,
          regle: reg.dateReglement ? "REGLE" : "NON REGLE",
          // Payload de validation allégé : le backend re-fetch le règlement par codeId.
          data: { codeId: reg.codeId, montant: reg.montant, dateReglement: reg.dateReglement },
          contrat: { codeId: contrat.codeId }
        });
      });
    });
    return Array.from(coms.values());
  };

  const setupReglementsOnMonth = (codeCompagnie, periode) => {
    setLoading(true);
    axiosInstance
      // Endpoint ALLÉGÉ (DTO plat via fonction SQL) : plus d'entités Contrat complètes (N+1/gros JSON)
      .get(`/api/contrat/organisme/month/cession/${codeCompagnie}`, {
        timeout: 120000, // évite l'attente infinie si l'endpoint stalle
        params: {
          codeCategorie: selectedCategorie ? selectedCategorie.codeCategorie : null,
          mois: date ? `${date}-01` : null
        }
      })
      .then((res) => {
        setReglementsByMonth(buildReglementRows(res.data, false));
      })
      .catch((err) => {
        console.error(err);
        setReglementsByMonth([]);
        toast.error("Erreur lors du chargement des contrats à céder.");
      })
      .finally(() => setLoading(false)); // garantit la fin du squelette même en cas d'erreur/timeout
  }

  // Charge les RÈGLEMENTS des mois ANTÉRIEURS jamais cédés (décochés par défaut).
  const setupPriorReglements = (codeCompagnie) => {
    axiosInstance
      .get(`/api/contrat/organisme/month/cession/${codeCompagnie}/anterieurs`, {
        timeout: 120000,
        params: {
          codeCategorie: selectedCategorie ? selectedCategorie.codeCategorie : null,
          mois: date ? `${date}-01` : null
        }
      })
      .then((res) => setPriorReglements(buildReglementRows(res.data, true)))
      .catch((err) => {
        console.error(err);
        setPriorReglements([]);
      });
  }

  // Charge les DÉCAISSEMENTS (sinistres payés / recours) des mois ANTÉRIEURS jamais cédés.
  const setupPriorDecaissements = (codeCompagnie) => {
    axiosInstance
      .get(`/api/decaissement/compagnie/${codeCompagnie}/anterieurs-non-cedes`, {
        params: {
          codeCategorie: selectedCategorie ? selectedCategorie.codeCategorie : null,
          mois: date ? `${date}-01` : null
        }
      })
      .then((res) => setPriorDecaissements((res.data || []).map((d) => ({ ...d, anterieur: true }))))
      .catch((err) => {
        console.error(err);
        setPriorDecaissements([]);
      });
  }

  const dateFiltering = (_, dateString) => {
    setDate(dateString);
  };

  const categorieFiltering = (_, categ) => {
    setSelectedCategorie(categ.data);
  };

  const compagnieFiltering = (code, comp) => {
    setSelectedCompagnie(comp.data);
  };

  // Calcul des totaux et indemnités
  const calculateTotals = () => {
    // Émissions/annulations : on part de reglementsByMonth (source get_contrats_reglements_cession,
    // la MÊME que les bordereaux) — les lignes portent la décomposition NETTE (PN Pool/Compagnie)
    // et restent disponibles même pour une cession déjà validée. On somme donc la PRIME NETTE,
    // ce qui garantit « total émissions = total prime nette » (= le total PN Pool du bordereau).
    // Repli si les parts nettes manquent : primeACeder(TTC) − accessoires − taxes.
    // Mois courant + éléments ANTÉRIEURS explicitement COCHÉS (sinon décochés → non comptés).
    // Les totaux doivent inclure les antérieurs cochés, puisqu'ils seront cédés (cession_code_id posé).
    const emissions = [
      ...(reglementsByMonth || []).filter((c) => (c.primeACeder || 0) >= 0),
      ...selectedEmissions.filter((c) => c.anterieur && (c.primeACeder || 0) >= 0),
    ];
    const annulations = [
      ...(reglementsByMonth || []).filter((c) => (c.primeACeder || 0) < 0),
      ...selectedAnnulations.filter((c) => c.anterieur && (c.primeACeder || 0) < 0),
    ];
    const sinistres = validationState.sinistresPayes.data;
    const recours = validationState.recoursEncaisses.data;

    // Prime nette CÉDÉE = PN Pool uniquement (la part compagnie en co-assurance est retenue, non cédée).
    const netteDe = (item) => {
        const pnp = Number(item?.primeNettePool);
        if (Number.isFinite(pnp)) {
            return pnp;
        }
        // Repli sans décomposition : net total (TTC − acc − taxes) — approx. quand la part compagnie est inconnue.
        return (Number(item?.primeACeder) || 0) - (Number(item?.accessoires) || 0) - (Number(item?.taxes) || 0);
    };
    const totalEmissions = emissions.reduce((sum, item) => sum + netteDe(item), 0);
    const totalAnnulations = Math.abs(annulations.reduce((sum, item) => sum + netteDe(item), 0));
    const indemnitesEmissions = totalEmissions * 0.15;
    const indemnitesAnnulations = totalAnnulations * 0.15;
    const totalSinistres = cessionExistante ? cessionExistante?.totalSinistres : sinistres.reduce((sum, item) => sum + (item.montant || 0), 0);
    const totalRecours = cessionExistante ? cessionExistante?.totalRecours : recours.reduce((sum, item) => sum + (item.montant || 0), 0);

    const debit = totalAnnulations + indemnitesEmissions + totalSinistres;
    const credit = totalEmissions + indemnitesAnnulations + totalRecours;
    const solde = debit - credit;

    return {
      totalEmissions,
      totalAnnulations,
      indemnitesEmissions,
      indemnitesAnnulations,
      totalSinistres,
      totalRecours,
      debit,
      credit,
      solde,
      soldeEnFaveurDe: solde > 0 ? 'Compagnie' : 'Pool TPV',
      soldeMontant: Math.abs(solde)
    };
  };

  const confirmValidationEmissions = () => {
    confirm({
      title: "Êtes-vous sûr de vouloir valider le bordereau des émissions?",
      icon: <ExclamationCircleFilled />,
      content: `${selectedEmissions.length} élément(s) sélectionné(s). Toutes vos actions sont historisées`,
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk() {
        dispatch({
          type: 'VALIDATE_EMISSIONS',
          // Mois : sélection explicite OU tout le mois. Antérieurs : SEULEMENT ceux cochés.
          payload: [
            ...(selectedEmissions.filter(x => !x.anterieur).length > 0
              ? selectedEmissions.filter(x => !x.anterieur)
              : reglementsByMonth.filter(c => c.primeACeder >= 0)),
            ...selectedEmissions.filter(x => x.anterieur),
          ]
        });
        toast.success("Bordereau des émissions validé");
      },
    });
  }

  const confirmValidationAnnulations = () => {
    confirm({
      title: "Êtes-vous sûr de vouloir valider le bordereau des annulations?",
      icon: <ExclamationCircleFilled />,
      content: `${selectedAnnulations.length} élément(s) sélectionné(s). Toutes vos actions sont historisées`,
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk() {
        dispatch({
          type: 'VALIDATE_ANNULATIONS',
          payload: [
            ...(selectedAnnulations.filter(x => !x.anterieur).length > 0
              ? selectedAnnulations.filter(x => !x.anterieur)
              : reglementsByMonth.filter(c => c.primeACeder < 0)),
            ...selectedAnnulations.filter(x => x.anterieur),
          ]
        });
        toast.success("Bordereau des annulations validé");
      },
    });
  }

  const confirmValidationSinistresPayes = () => {
    confirm({
      title: "Êtes-vous sûr de vouloir valider le bordereau des sinistres payés?",
      icon: <ExclamationCircleFilled />,
      content: `${selectedSinistres.length} élément(s) sélectionné(s). Toutes vos actions sont historisées`,
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk() {
        // Sinistres payés = SORTIES (pas recours) AVEC un chèque assigné (numeroCheque) = réellement payés.
        const sinistresPayesMois = decaissements.filter(d => !d.encaissement && d.numeroCheque);
        dispatch({
          type: 'VALIDATE_SINISTRES',
          payload: [
            ...(selectedSinistres.filter(x => !x.anterieur).length > 0
              ? selectedSinistres.filter(x => !x.anterieur)
              : sinistresPayesMois),
            ...selectedSinistres.filter(x => x.anterieur),
          ]
        });
        toast.success("Bordereau des sinistres payés validé");
      },
    });
  }

  const confirmValidationRecoursSinistres = () => {
    confirm({
      title: "Êtes-vous sûr de vouloir valider le bordereau des recours encaissés?",
      icon: <ExclamationCircleFilled />,
      content: `${selectedRecours.length} élément(s) sélectionné(s). Toutes vos actions sont historisées`,
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk() {
        const recoursEncaissesMois = decaissements.filter(d => d.encaissement);
        dispatch({
          type: 'VALIDATE_RECOURS',
          payload: [
            ...(selectedRecours.filter(x => !x.anterieur).length > 0
              ? selectedRecours.filter(x => !x.anterieur)
              : recoursEncaissesMois),
            ...selectedRecours.filter(x => x.anterieur),
          ]
        });
        toast.success("Bordereau des recours encaissés validé");
      },
    });
  }

  // Option 1 — « re-valider » une cession DÉJÀ validée : on renvoie les totaux recalculés en
  // prime nette (calculateTotals recalcule depuis les lignes fraîches) pour corriger la valeur
  // stockée en base (et le chèque s'il n'est pas encore versé).
  const handleRecalculerNette = () => {
    const codeId = cessionExistante?.codeId;
    if (!codeId) {
      toast.error("Cession introuvable");
      return;
    }
    const totals = calculateTotals();
    confirm({
      title: "Recalculer cette cession en prime nette ?",
      icon: <ExclamationCircleFilled />,
      content: "Les totaux enregistrés (souscriptions, indemnités, solde) seront recalculés sur la prime nette. Le montant du chèque n'est ajusté que s'il n'a pas encore été versé.",
      okText: "Recalculer",
      cancelText: "Annuler",
      onOk() {
        setRecalculLoading(true);
        return axiosInstance
          .put(`/api/cession/${codeId}/recalculer-nette`, {
            totalEmissions: totals.totalEmissions,
            totalAnnulations: totals.totalAnnulations,
            indemnitesEmissions: totals.indemnitesEmissions,
            indemnitesAnnulations: totals.indemnitesAnnulations,
            debit: totals.debit,
            credit: totals.credit,
            solde: totals.solde,
            soldeMontant: totals.soldeMontant,
          })
          .then(() => {
            toast.success("Cession recalculée en prime nette");
            window.location.reload();
          })
          .catch(() => toast.error("Échec du recalcul"))
          .finally(() => setRecalculLoading(false));
      },
    });
  };

  const confirmValidationCession = () => {
    confirm({
      title: "Êtes-vous sûr de vouloir valider la cession?",
      icon: <ExclamationCircleFilled />,
      content: "Cette action est définitive. Toutes vos actions sont historisées",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk() {
        validerCession();
      },
    });
  };

  const validerCession = () => {
    setAddCessionLoading(true);

    const totals = calculateTotals();

    const cessionData = {
      utilisateur: LOGGED_USER,
      moisRapport: `${date}-01`,
      categorie: selectedCategorie,
      compagnie: selectedCompagnie,
      bordereauEmissions: {
        items: validationState.emissions.data,
        total: totals.totalEmissions
      },
      bordereauAnnulations: {
        items: validationState.annulations.data,
        total: totals.totalAnnulations
      },
      bordereauSinistresPayes: {
        items: validationState.sinistresPayes.data,
        total: totals.totalSinistres
      },
      bordereauRecoursEncaisses: {
        items: validationState.recoursEncaisses.data,
        total: totals.totalRecours
      },
      compteCession: {
        totalEmissions: totals.totalEmissions,
        totalAnnulations: totals.totalAnnulations,
        indemnitesEmissions: totals.indemnitesEmissions,
        indemnitesAnnulations: totals.indemnitesAnnulations,
        totalSinistresPayes: totals.totalSinistres,
        totalRecoursEncaisses: totals.totalRecours,
        debit: totals.debit,
        credit: totals.credit,
        solde: totals.solde,
        soldeEnFaveurDe: totals.soldeEnFaveurDe
      }
    };

    console.log(`CessionData: `, cessionData);

    axiosInstance
      .post("/api/cession/dto", cessionData)
      .then((res) => {
        dispatch({ type: 'VALIDATE_CESSION', payload: cessionData });
        toast.success("Cession validée avec succès!");
        setAddCessionLoading(false);
        setCessionExistante(res.data);
      })
      .catch((err) => {
        console.log(err);
        // 409 = garde-fou anti-doublon backend : cette catégorie est déjà cédée pour ce mois
        // (double-clic / retry). include-message=never masque le motif → on mappe par statut.
        if (err?.response?.status === 409) {
          toast.error("Cette catégorie a déjà été cédée pour ce mois. Rafraîchissez la liste des cessions validées.");
        } else {
          toast.error("Erreur lors de la validation de la cession");
        }
        setAddCessionLoading(false);
      });
  };

  const checkCession = (mois, compagnie, categorie) => {
    axiosInstance
      .get(`/api/cession/checking-cession-dto/${mois}/${compagnie}/${categorie}`)
      .then((res) => {
        console.log("Existante: ", res.data);
        setCessionExistante(res.data);
        if (res.data) {
          dispatch({ type: 'VALIDATE_CESSION', payload: res.data });
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const onTabsChange = (key) => {
    const allBordereauxValidated =
      validationState.emissions.validated &&
      validationState.annulations.validated &&
      validationState.sinistresPayes.validated &&
      validationState.recoursEncaisses.validated;

    calculateTotals();
    if (key === "5" && !allBordereauxValidated && !cessionExistante) {

      toast.warning(
        "Veuillez valider tous les bordereaux avant d'accéder au compte de cession"
      );
      return;
    }
    setTabId(key);
  };

  const primesColumns = [
    {
      title: "Origine",
      key: "origine",
      render: (_, r) => r.anterieur
        ? <Tag color="orange">Mois antérieur{r.effet ? " · " + new Date(r.effet).toLocaleDateString("fr-FR", { month: "2-digit", year: "numeric" }) : ""}</Tag>
        : <Tag color="green">Mois courant</Tag>,
    },
    {
      title: "N° Police",
      dataIndex: "numeroPolice",
      key: "numeroPolice",
    },
    {
      title: "Avenant",
      dataIndex: "avenant",
      key: "avenant",
    },
    {
      title: "Assuré",
      dataIndex: "assure",
      key: "assure",
    },
    {
      title: "Effet",
      dataIndex: "effet",
      key: "effet",
      render: (effet) => <span>{effet ? new Date(effet).toLocaleDateString("fr-FR") : "-"}</span>,
    },
    {
      title: "Échéance",
      dataIndex: "echeance",
      key: "echeance",
      render: (echeance) => (
        <span>{echeance ? new Date(echeance).toLocaleDateString("fr-FR") : "-"}</span>
      ),
    },
    {
      title: "Catégorie",
      dataIndex: "categorie",
      key: "categorie",
    },
    // Décomposition du montant cédé (proratisée côté serveur, somme = Prime TTC). Affichée en
    // valeur absolue pour rester cohérent avec l'ancienne colonne unique (les annulations, de
    // montant négatif, y sont présentées en magnitude).
    {
      title: "PN Pool",
      dataIndex: "primeNettePool",
      key: "primeNettePool",
      align: "right",
      render: (v) => <span>{displayNumbers(Math.abs(v || 0))}</span>,
    },
    {
      title: "PN Compagnie",
      dataIndex: "primeNetteCompagnie",
      key: "primeNetteCompagnie",
      align: "right",
      render: (v) => <span>{displayNumbers(Math.abs(v || 0))}</span>,
    },
    {
      title: "Accessoires",
      dataIndex: "accessoires",
      key: "accessoires",
      align: "right",
      render: (v) => <span>{displayNumbers(Math.abs(v || 0))}</span>,
    },
    {
      title: "Taxes",
      dataIndex: "taxes",
      key: "taxes",
      align: "right",
      render: (v) => <span>{displayNumbers(Math.abs(v || 0))}</span>,
    },
    {
      title: "Prime TTC",
      dataIndex: "primeACeder",
      key: "primeACeder",
      align: "right",
      render: (v) => <span><strong>{displayNumbers(Math.abs(v || 0))}</strong> F CFA</span>,
    },
  ];

  const sinistresColumns = [
    {
      title: "Origine",
      key: "origine",
      render: (_, r) => r.anterieur
        ? <Tag color="orange">Mois antérieur{r.dateReglement ? " · " + new Date(r.dateReglement).toLocaleDateString("fr-FR", { month: "2-digit", year: "numeric" }) : ""}</Tag>
        : <Tag color="green">Mois courant</Tag>,
    },
    {
      title: "N° Sinistre",
      dataIndex: "numeroSinistre",
      key: "numeroSinistre",
      render: (_, record) => <span>{record?.sinistreNumeroSinistre || record?.sinistreNumSinistre}</span>
    },
    {
      title: "N° Police",
      dataIndex: "numeroPolice",
      key: "numeroPolice",
      render: (_, record) => <span>{record?.sinistreNumPolice || record?.sinistreNumPoliceP}</span>
    },
    {
      title: "Assuré",
      dataIndex: "sinistreSouscripteur",
      key: "sinistreSouscripteur",
    },
    {
      title: "Date de survenance",
      dataIndex: "dateSinistre",
      key: "dateSinistre",
      render: (v) => <span>{v ? frenchDateOnly(v) : ""}</span>,
    },
    {
      title: "Catégorie",
      dataIndex: "categorie",
      key: "categorie",
      render: () => <span>{selectedCategorie?.codeCategorie}</span>
    },
    {
      title: "Justificatif",
      dataIndex: "justificatif",
      key: "justificatif",
      render: (_, record) => {
        const j = formatJustificatifCheque(record);
        return j ? <span style={{ whiteSpace: "pre-line" }}>{j}</span> : <span style={{ color: "#bbb" }}>—</span>;
      },
    },
    {
      title: "Montant",
      dataIndex: "montant",
      key: "montant",
      render: (montant) => <span>{displayNumbers(montant)} F CFA</span>
    },
  ];

  // Mois courant + éléments des mois ANTÉRIEURS non cédés (tag « Antérieur », décochés par défaut).
  // ⚠️ DÉDUP : la fonction du mois liste TOUS les règlements d'un contrat matché (y compris un vieux
  // règlement non cédé) ; on retire donc des antérieurs ceux déjà présents dans le mois (par codeId
  // réel du règlement / décaissement) pour éviter un doublon (et un double-cession).
  const monthRegCodeIds = new Set(reglementsByMonth.map(r => r.data?.codeId).filter(Boolean));
  const priorReglementsDedup = priorReglements.filter(r => !monthRegCodeIds.has(r.data?.codeId));
  const monthDecCodeIds = new Set(decaissements.map(d => d.codeId).filter(Boolean));
  const priorDecaissementsDedup = priorDecaissements.filter(d => !monthDecCodeIds.has(d.codeId));

  const emissions = [
    ...reglementsByMonth.filter(c => c.primeACeder >= 0),
    ...priorReglementsDedup.filter(c => c.primeACeder >= 0),
  ];
  const annulations = [
    ...reglementsByMonth.filter(c => c.primeACeder < 0),
    ...priorReglementsDedup.filter(c => c.primeACeder < 0),
  ];
  // Sinistres payés = SORTIES (pas recours) AVEC un chèque assigné (numeroCheque) = réellement payés.
  const sinistresPayes = [
    ...decaissements.filter(d => !d.encaissement && d.numeroCheque),
    ...priorDecaissementsDedup.filter(d => !d.encaissement && d.numeroCheque),
  ];
  const recoursEncaisses = [
    ...decaissements.filter(d => d.encaissement),
    ...priorDecaissementsDedup.filter(d => d.encaissement),
  ];

  const isCompagnieUser = selectedCompagnie.code === LOGGED_USER.profil.organisme.code;
  const isCessionValidated = validationState.cessionValidated || cessionExistante;

  return (
    <div className="layout-wrapper layout-content-navbar">
      <style>{`
        /* Palette de couleurs personnalisée */
        :root {
          --color-primary: #F39C12;
          --color-primary-light: #FFF3E0;
          --color-primary-dark: #A66E00;
          --color-text: #2D2D2D;
          --color-bg-neutral: #F2F0ED;
          --color-shadow: rgba(243, 156, 18, 0.15);
          --color-border: rgba(243, 156, 18, 0.2);
        }

        /* Cards avec design moderne */
        .cession-filter-card {
          background: white;
          border-radius: 16px;
          border: 1px solid var(--color-border);
          box-shadow: 0 2px 12px var(--color-shadow);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          overflow: hidden;
        }

        .cession-filter-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px var(--color-shadow);
          border-color: var(--color-primary);
        }

        .cession-filter-card .card-body {
          padding: 24px;
        }

        .cession-filter-label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: var(--color-text);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 12px;
          position: relative;
          padding-left: 12px;
        }

        .cession-filter-label::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 4px;
          height: 16px;
          background: linear-gradient(180deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
          border-radius: 2px;
        }

        /* Main card avec gradient subtil */
        .cession-main-card {
          background: white;
          border-radius: 20px;
          border: none;
          box-shadow: 0 4px 20px var(--color-shadow);
          overflow: hidden;
        }

        .cession-main-card .card-header {
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
          padding: 28px 32px;
          border: none;
          position: relative;
          overflow: hidden;
        }

        .cession-main-card .card-header::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -10%;
          width: 300px;
          height: 300px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
        }

        .cession-header-content {
          position: relative;
          z-index: 1;
        }

        .cession-main-card .card-header h5 {
          color: white;
          font-size: 22px;
          font-weight: 700;
          margin: 0;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .cession-validated-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(10px);
          color: white;
          padding: 8px 20px;
          border-radius: 50px;
          font-size: 13px;
          font-weight: 600;
          margin-top: 12px;
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .cession-validated-badge::before {
          content: '✓';
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          background: white;
          color: var(--color-primary);
          border-radius: 50%;
          font-weight: bold;
        }

        /* Tabs personnalisés */
        .cession-tabs .ant-tabs-nav {
          margin-bottom: 0;
          background: var(--color-bg-neutral);
          padding: 8px;
          border-radius: 12px;
        }

        .cession-tabs .ant-tabs-tab {
          background: transparent;
          border: none;
          border-radius: 8px;
          padding: 12px 20px;
          margin: 0 4px;
          color: var(--color-text);
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .cession-tabs .ant-tabs-tab:hover {
          background: rgba(243, 156, 18, 0.1);
          color: var(--color-primary);
        }

        .cession-tabs .ant-tabs-tab-active {
          background: white;
          color: var(--color-primary);
          box-shadow: 0 2px 8px var(--color-shadow);
        }

        .cession-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: var(--color-primary);
          font-weight: 600;
        }

        .cession-tabs .ant-tabs-ink-bar {
          display: none;
        }

        /* Customisation des Select et DatePicker */
        .cession-filter-card .ant-select-selector,
        .cession-filter-card .ant-picker {
          border-radius: 10px;
          border: 2px solid var(--color-border);
          min-height: 44px;
          transition: all 0.3s ease;
        }

        .cession-filter-card .ant-select-selector:hover,
        .cession-filter-card .ant-picker:hover {
          border-color: var(--color-primary);
        }

        .cession-filter-card .ant-select-focused .ant-select-selector,
        .cession-filter-card .ant-picker-focused {
          border-color: var(--color-primary);
          box-shadow: 0 0 0 3px var(--color-primary-light);
        }

        /* Icône de validation dans les tabs */
        .tab-validation-icon {
          display: inline-block;
          width: 18px;
          height: 18px;
          background: var(--color-primary);
          color: white;
          border-radius: 50%;
          text-align: center;
          line-height: 18px;
          font-size: 11px;
          margin-left: 8px;
          font-weight: bold;
        }

        /* Responsive adjustments */
        @media (max-width: 768px) {
          .cession-filter-card .card-body {
            padding: 16px;
          }

          .cession-main-card .card-header {
            padding: 20px;
          }

          .cession-main-card .card-header h5 {
            font-size: 18px;
          }
        }

        /* Animation de chargement */
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.6;
          }
        }

        .loading-pulse {
          animation: pulse 1.5s ease-in-out infinite;
        }
      `}</style>

      <div className="layout-container">
        <SideBar />

        <div className="layout-page">
          <NavBar />

          <div className="content-wrapper" style={{ background: 'var(--color-bg-neutral)' }}>
            <div className="container-xxl flex-grow-1 container-p-y">

              {/* Filtres avec design amélioré */}
              <div className="row g-4 mb-4">
                <div className="col-sm-6 col-xl-3">
                  <div className="cession-filter-card">
                    <div className="card-body">
                      <span className="cession-filter-label">Période</span>
                      <DatePicker
                        placeholder="Sélectionner le mois"
                        onChange={dateFiltering}
                        picker="month"
                        disabled={isCessionValidated}
                        style={{ width: '100%' }}
                      />
                    </div>
                  </div>
                </div>

                <div className="col-sm-6 col-xl-3">
                  <div className="cession-filter-card">
                    <div className="card-body">
                      <span className="cession-filter-label">Catégorie</span>
                      <Select
                        placeholder="Sélectionner la catégorie"
                        style={{ width: "100%" }}
                        onChange={categorieFiltering}
                        disabled={isCessionValidated}
                        options={categories.map((cat) => ({
                          value: cat.codeCategorie,
                          label: cat.nom,
                          data: cat,
                        }))}
                      />
                    </div>
                  </div>
                </div>

                {LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL" && (
                  <div className="col-sm-6 col-xl-3">
                    <div className="cession-filter-card">
                      <div className="card-body">
                        <span className="cession-filter-label">Compagnie</span>
                        <Select
                          placeholder="Sélectionner la compagnie"
                          style={{ width: "100%" }}
                          onChange={compagnieFiltering}
                          disabled={isCessionValidated}
                          options={compagnies.map((com) => ({
                            value: com.code,
                            label: com.raisonSociale,
                            data: com,
                          }))}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Contenu principal avec design moderne */}
              <div className="row g-4">
                <div className="col-12">
                  <div className="cession-main-card">
                    <div className="card-header">
                      <div className="cession-header-content">
                        <h5>
                          {`Cession du Mois: ${date ? frenchMonth(date.split("-")[1]) + " " + date.split("-")[0] : "N/A"} | Catégorie: ${selectedCategorie?.codeCategorie || "N/A"}`}
                        </h5>
                        {isCessionValidated && (
                          <span className="cession-validated-badge">
                            Cession Validée
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="card-body pt-0">
                      <ConfigProvider
                        locale={frFR}
                        theme={{
                          token: {
                            colorPrimary: '#F39C12',
                            borderRadius: 8,
                          },
                        }}
                      >
                        <Skeleton loading={loading} active>
                          <div className="cession-tabs">
                            <Tabs
                              onChange={onTabsChange}
                              type="card"
                              activeKey={tabId}
                              items={[
                                {
                                  label: (
                                    <span>
                                      Bordereau des émissions
                                      {validationState.emissions.validated && (
                                        <span className="tab-validation-icon">✓</span>
                                      )}
                                    </span>
                                  ),
                                  key: "1",
                                  children: (
                                    <BordereauEmissions
                                      ref={printEmissionsRef}
                                      data={emissions}
                                      columns={primesColumns}
                                      selectedRows={selectedEmissions}
                                      onSelectionChange={setSelectedEmissions}
                                      onValidate={confirmValidationEmissions}
                                      onPrint={handlePrintEmissions}
                                      isValidated={validationState.emissions.validated}
                                      isDisabled={!isCompagnieUser || isCessionValidated}
                                      loading={addCessionLoading}
                                      categorie={selectedCategorie}
                                      periode={date}
                                      compagnie={selectedCompagnie}
                                    />
                                  ),
                                },
                                {
                                  label: (
                                    <span>
                                      Bordereau des annulations
                                      {validationState.annulations.validated && (
                                        <span className="tab-validation-icon">✓</span>
                                      )}
                                    </span>
                                  ),
                                  key: "2",
                                  children: (
                                    <BordereauAnnulations
                                      ref={printAnnulationsRef}
                                      data={annulations}
                                      columns={primesColumns}
                                      selectedRows={selectedAnnulations}
                                      onSelectionChange={setSelectedAnnulations}
                                      onValidate={confirmValidationAnnulations}
                                      onPrint={handlePrintAnnulations}
                                      isValidated={validationState.annulations.validated}
                                      isDisabled={!isCompagnieUser || isCessionValidated}
                                      loading={addCessionLoading}
                                      categorie={selectedCategorie}
                                      periode={date}
                                      compagnie={selectedCompagnie}
                                    />
                                  ),
                                },
                                {
                                  label: (
                                    <span>
                                      Bordereau des sinistres payés
                                      {validationState.sinistresPayes.validated && (
                                        <span className="tab-validation-icon">✓</span>
                                      )}
                                    </span>
                                  ),
                                  key: "3",
                                  children: (
                                    <BordereauSinistresPayes
                                      ref={printSinistresRef}
                                      data={sinistresPayes}
                                      columns={sinistresColumns}
                                      selectedRows={selectedSinistres}
                                      onSelectionChange={setSelectedSinistres}
                                      onValidate={confirmValidationSinistresPayes}
                                      onPrint={handlePrintSinistres}
                                      isValidated={validationState.sinistresPayes.validated}
                                      isDisabled={!isCompagnieUser || isCessionValidated}
                                      loading={addCessionLoading}
                                      categorie={selectedCategorie}
                                      periode={date}
                                      compagnie={selectedCompagnie}
                                    />
                                  ),
                                },
                                {
                                  label: (
                                    <span>
                                      Bordereau des recours encaissés
                                      {validationState.recoursEncaisses.validated && (
                                        <span className="tab-validation-icon">✓</span>
                                      )}
                                    </span>
                                  ),
                                  key: "4",
                                  children: (
                                    <BordereauRecoursEncaisses
                                      ref={printRecoursRef}
                                      data={recoursEncaisses}
                                      columns={sinistresColumns}
                                      selectedRows={selectedRecours}
                                      onSelectionChange={setSelectedRecours}
                                      onValidate={confirmValidationRecoursSinistres}
                                      onPrint={handlePrintRecours}
                                      isValidated={validationState.recoursEncaisses.validated}
                                      isDisabled={!isCompagnieUser || isCessionValidated}
                                      loading={addCessionLoading}
                                      categorie={selectedCategorie}
                                      periode={date}
                                      compagnie={selectedCompagnie}
                                    />
                                  ),
                                },
                                {
                                  label: "Compte de cession",
                                  key: "5",
                                  children: (
                                    <CompteDeCession
                                      ref={printCessionRef}
                                      validationState={validationState}
                                      cessionExistante={cessionExistante}
                                      totals={calculateTotals()}
                                      onValidate={confirmValidationCession}
                                      onPrint={handlePrintCession}
                                      onRecalculer={handleRecalculerNette}
                                      recalculLoading={recalculLoading}
                                      isValidated={isCessionValidated}
                                      isDisabled={!isCompagnieUser}
                                      loading={addCessionLoading}
                                      categorie={selectedCategorie}
                                      periode={date}
                                      compagnie={selectedCompagnie}
                                    />
                                  ),
                                },
                              ]}
                            />
                          </div>
                        </Skeleton>
                      </ConfigProvider>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Footer />
            <div className="content-backdrop fade"></div>
          </div>
        </div>
      </div>

      <div className="layout-overlay layout-menu-toggle"></div>
      {/* {LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL" && (
        <div class="buy-now">
          <button
            onClick={() => navigate('/cessions-pool')}
            class="btn btn-secondary btn-buy-now"
            style={{
              backgroundColor: '#242166',
              borderColor: '#242166',
              fontWeight: 500,
              fontSize: '15px',
              padding: '9px 24px',
              borderRadius: '10px',
              boxShadow: '0 6px 20px rgba(36, 33, 102, 0.4)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <CheckOutlined style={{ fontSize: '18px' }} />
            Cession validées
          </button>
        </div>
      )} */}
      <div className="drag-target"></div>
    </div>
  );
};

export default Cession;